import { miscConstants, preferenceConstants } from '../constants';
import { Language, PriceChartPeriod, Region } from '../shared/types';

const localStorageKey = 'preference';

export const key_priceViewerVolumeOverlap = 'priceViewerVolumeOverlap';
export const key_priceViewerTurnoverOverlap = 'priceViewerTurnoverOverlap';
export const key_showParticipantsNumber = 'showParticipantsNumber';
export const key_showShiftedParticipantsNumber =
  'showShiftedParticipantsNumber';
export const key_showMA = 'showMA';
export const key_MAType = 'MAType';
export const key_MAIntervals = 'MAIntervals';
export const key_showRSI = 'showRSI';
export const key_RSIInterval = 'RSIInterval';
export const key_showATR = 'showATR';
export const key_ATRInterval = 'ATRInterval';
export const key_showMCAD = 'showMCAD';
export const key_showBollingerBand = 'showBollingerBand';
export const key_lang = 'lang';
export const key_region = 'region';
export const key_period = 'period';
export const key_unit = 'unit';
export const key_minuteUnit = 'minuteUnit';
export const key_fontSize = 'fontSize';
export const key_priceChangeInterval = 'priceChangeInterval';
export const key_showTopN = 'showTopN';
export const key_priceViewerTopN = 'priceViewerTopN';
export const key_topN = 'topN';
export const key_priceRangeInterval = 'priceRangeInterval';
export const key_avgVolInterval = 'avgVolInterval';
export const key_participantNumChangeInterval = 'participantNumChangeInterval';
export const key_usStatisticsPageFilters = 'usStatisticsPageFilters';
export const key_hkStatisticsPageFilters = 'hkStatisticsPageFilters';
export const key_stockTempPriceV2 = 'stockTempPriceV2';
export const key_percentChangeV2 = 'percentChangeV2';
export const key_stockTempTurnoverV2 = 'stockTempTurnoverV2';
export const key_stockTempMktcapV2 = 'stockTempMktcapV2';
export const key_returnTillNowV2 = 'returnTillNowV2';
export const key_changePercentageColumnV2 = 'changePercentageColumnV2';
export const key_showIndexSMA = 'showIndexSMA';
export const key_showHSIFundamental = 'showHSIFundamental';
export const key_indexSMAIntervals = 'indexSMAIntervals';
export const key_showMarketSMA = 'showMarketSMA';
export const key_marketSMAIntervals = 'marketSMAIntervals';
export const key_graphColorStyle = 'graphColorStyle';
export const key_nopColorStyleV2 = 'nopColorStyleV2';
export const key_concentrationColorStyleV2 = 'concentrationColorStyleV2';
export const key_strategyMode = 'strategyMode';
export const key_comparisonColorStyle = 'comparisonColorStyle';
export const key_showWatchingList = 'showWatchingList';
export const key_histogramYellowSMA5 = 'histogramYellowSMA5';
export const key_hkHistogramIndexType = 'hkHistogramIndexType';
export const key_usHistogramIndexType = 'usHistogramIndexType';
export const key_histogramGroupType = 'histogramGroupType';
export const key_showRetailTrades = 'showRetailTrades';
export const key_showInstitutionalTrades = 'showInstitutionalTrades';
export const key_priceViewerLabelType = 'priceViewerLabelType';
export const key_retailTradesVersion = 'retailTradesVersion';
export const key_institutionalTradesVersion = 'institutionalTradesVersion';
export const key_zoneActiveZones = 'zoneActiveZones';
export const key_popUpNoticeModal = 'popUpNoticeModal';
export const key_showFearGreedIndex = 'showFearGreedIndex';
export const key_showAAII = 'showAAII';
export const key_showSouthbound = 'showSouthbound';
export const key_showNOPStrategy = 'showNOPStrategy';
export const key_NOPStrategyVersion = 'NOPStrategyVersion';
export const key_showHSIPE = 'showHSIPE';
export const key_showHSIPB = 'showHSIPB';
export const key_showHSIShortRatio = 'showHSIShortRatio';
export const key_showHSIDividend = 'showHSIDividend';
const DEFAULT_LANG: Language = 'zh';

const processSaved = state => {
  if (!state) {
    return state;
  }

  // prevent double NOP selection
  // https://app.clubhouse.io/datalouder/story/2567/we-can-still-see-double-selection-of-nop-from-some-users
  if (
    state[key_showParticipantsNumber] &&
    state[key_showShiftedParticipantsNumber]
  ) {
    return {
      ...state,
      [key_showParticipantsNumber]: true,
      [key_showShiftedParticipantsNumber]: false,
    };
  }
  return state;
};

const saved = JSON.parse(localStorage.getItem(localStorageKey));
const initialState = saved ? { ...processSaved(saved) } : {};

export type SMAInterval = 50 | 150 | 200;
export type MAType = 'sma' | 'ema';

const defaults = {
  //set the priceViewerOverlap be false if using mobile
  [key_priceViewerVolumeOverlap]:
    document.documentElement.clientWidth > 576 ? true : false,
  [key_priceViewerTurnoverOverlap]:
    document.documentElement.clientWidth > 576 ? true : false,
  // participants number
  [key_showParticipantsNumber]: true,
  // shifted participants number
  [key_showShiftedParticipantsNumber]: false,
  // moving average
  [key_showMA]: true,
  [key_MAType]: 'sma' as MAType,
  [key_MAIntervals]: [20, 50],
  // index constituent sma
  [key_showIndexSMA]: true,
  [key_showHSIFundamental]: true,
  [key_indexSMAIntervals]: [50, 150, 200] as SMAInterval[],
  // market constituent sma
  [key_showMarketSMA]: true,
  [key_marketSMAIntervals]: [50, 150, 200] as SMAInterval[],
  // RSI
  [key_showRSI]: false,
  [key_RSIInterval]: 14,
  // ATR
  [key_showATR]: false,
  [key_ATRInterval]: 14,
  // MCAD
  [key_showMCAD]: false,
  // Bollinger Band
  [key_showBollingerBand]: false,
  // language
  [key_lang]: DEFAULT_LANG as Language,
  // region
  [key_region]: 'hk' as Region,
  // period
  [key_period]: '6m' as PriceChartPeriod,
  // unit
  [key_unit]: 'd',
  // minute-unit
  [key_minuteUnit]: '1m',
  // font size
  [key_fontSize]: 'small',
  //price Change Interval
  [key_priceChangeInterval]: '1d',
  // topN
  [key_priceViewerTopN]: 10,
  [key_showTopN]: true,
  [key_topN]: 10,
  //price Range Interval
  [key_priceRangeInterval]: '52w',
  // average volume interval
  [key_avgVolInterval]: '3m',
  //
  [key_participantNumChangeInterval]: '5d',
  //
  [key_usStatisticsPageFilters]: [
    {
      id: 'stockCode',
      value: '',
    },
  ],
  //
  [key_hkStatisticsPageFilters]: [
    {
      id: 'turnover',
      value: {
        operator: '>',
        operand: 500_000,
      },
    },
  ],
  [key_graphColorStyle]: 0,
  [key_nopColorStyleV2]: '#228B2280',
  [key_concentrationColorStyleV2]: '#6495ed80',
  [key_comparisonColorStyle]: 'orange',
  [key_showWatchingList]: true,
  [key_histogramYellowSMA5]: true,
  [key_usHistogramIndexType]: ['INX'],
  [key_hkHistogramIndexType]: ['HSI'],
  [key_histogramGroupType]: ['0', '1', '2', '3'],
  [key_showRetailTrades]: false,
  [key_showInstitutionalTrades]: false,
  [key_priceViewerLabelType]: 'userWatchingList',
  [key_retailTradesVersion]: 'v2',
  [key_institutionalTradesVersion]: 'v2',
  [key_zoneActiveZones]: ['S', 'A', 'B', 'C', 'D', 'E'],
  [key_popUpNoticeModal]: true,
  [key_showFearGreedIndex]: false,
  [key_showAAII]: false,
  [key_showSouthbound]: false,
  [key_showNOPStrategy]: false,
  [key_NOPStrategyVersion]: 'alpha',
  [key_strategyMode]: 'simple-mode',
  [key_showHSIPE]: false,
  [key_showHSIPB]: false,
  [key_showHSIDividend]: false,
  [key_showHSIShortRatio]: false,
  [key_stockTempPriceV2]: {
    us: { ranking: '', lookup: '' },
    hk: { ranking: '', lookup: '' },
  },
  [key_percentChangeV2]: {
    us: { ranking: '', lookup: '' },
    hk: { ranking: '', lookup: '' },
  },
  [key_stockTempTurnoverV2]: {
    us: { ranking: '', lookup: '' },
    hk: { ranking: '', lookup: '' },
  },
  [key_stockTempMktcapV2]: {
    us: { ranking: '', lookup: '' },
    hk: { ranking: '', lookup: '' },
  },
  [key_returnTillNowV2]: {
    us: { ranking: '', lookup: '' },
    hk: { ranking: '', lookup: '' },
  },
  [key_changePercentageColumnV2]: {
    us: { ranking: '', lookup: '' },
    hk: { ranking: '', lookup: '' },
  },
};

export type PreferenceStates = typeof defaults;

function saveToLocalStorageAndWindow(state) {
  localStorage.setItem(localStorageKey, JSON.stringify(state));
  window['_DLPrefs'] = state;
  return state;
}

export default (
  state = { ...defaults, ...initialState },
  action
): PreferenceStates => {
  switch (action.type) {
    case preferenceConstants.SET_PREFERENCE: {
      return saveToLocalStorageAndWindow({
        ...state,
        [action.key]: action.value,
      });
    }
    case miscConstants.LOAD_PREF_SUCCESS: {
      return saveToLocalStorageAndWindow({
        ...state,
        ...action.preference,
      });
    }
    default: {
      return state;
    }
  }
};

export const getLangFromWindow = (): Language => {
  const _dlPrefs = window['_DLPrefs'];
  if (!_dlPrefs) return DEFAULT_LANG;
  const lang = _dlPrefs[key_lang];
  if (!lang) return DEFAULT_LANG;
  if (!['zh', 'en'].includes(lang)) {
    return DEFAULT_LANG;
  }
  return lang as Language;
};
